import React, { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { addMonths, differenceInCalendarDays, format, parse } from 'date-fns';
import { CostSummary } from './CostSummary';
import { ApplyFormData } from './ApplyFormComponent1';
import { useLocation } from 'react-router-dom';

interface MonthlyRental {
  id: number;
  room_type_id: number;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string | null;
}

interface EstimateFormProps {
  price: number;
  monthlyRentals: MonthlyRental[];
  capacity: number;
}

const truncateToHundreds = (value: number) => {
  return Math.floor(value / 100) * 100;
};

// 「YYYY-MM-DD」文字列を常にローカル日付として解釈
const parseLocalDate = (s: string) => parse(s, 'yyyy-MM-dd', new Date());

function isDateRangeFullyAvailable(
  from: Date,
  to: Date,
  availableRanges: { start: Date; end: Date }[]
): boolean {
  if (from > to) return false;
  const current = new Date(from);
  while (current <= to) {
    const inSomeRange = availableRanges.some(
      (range) => current >= range.start && current <= range.end
    );
    if (!inSomeRange) return false;
    current.setDate(current.getDate() + 1);
  }
  return true;
}

function checkSpecialRule(fromD: Date, toD: Date): boolean {
  const dayDiff = differenceInCalendarDays(toD, fromD);
  // 例: 29 日だけ特別に許可
  if (dayDiff === 29) {
    const fromPlus29 = new Date(fromD);
    fromPlus29.setDate(fromPlus29.getDate() + 29);
    if (fromPlus29.getTime() === toD.getTime()) {
      return true;
    }
  }
  return false;
}

export const EstimateForm: React.FC<EstimateFormProps> = ({
  price,
  monthlyRentals,
  capacity,
}) => {
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const {
    register,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<ApplyFormData>();

  // monthlyRentals → availableRanges に変換（ローカル解釈）
  const availableRanges = useMemo(
    () =>
      monthlyRentals.map((r) => {
        const d0 = r.start_date.split('T')[0];
        const d1 = r.end_date.split('T')[0];
        return { start: parseLocalDate(d0), end: parseLocalDate(d1) };
      }),
    [monthlyRentals]
  );

  // 表示用の文字列（文字列のまま）
  const availableDatesPeriod = useMemo(
    () =>
      monthlyRentals.map((rental) => ({
        period: `${rental.start_date.split('T')[0]} ~ ${rental.end_date.split('T')[0]}`,
        status: useEnglish ? 'available' : '入居可',
      })),
    [monthlyRentals, useEnglish]
  );

  const fromDateValue = watch('estimateData.fromDate');
  const toDateValue = watch('estimateData.toDate');

  useEffect(() => {
    if (!fromDateValue || !toDateValue) {
      clearErrors('estimateData.period');
      return;
    }
    const fromD = parseLocalDate(fromDateValue);
    const toD = parseLocalDate(toDateValue);
    if (isNaN(fromD.getTime()) || isNaN(toD.getTime())) {
      clearErrors('estimateData.period');
      return;
    }

    // (1) from > to
    if (fromD > toD) {
      setErrorIfDiff(
        useEnglish
          ? 'The end date must be after the start date.'
          : '終了日は開始日より後の日付を選択してください。'
      );
      return;
    }

    // (2) 30日以上 or 特例
    const dayDiff = differenceInCalendarDays(toD, fromD);
    if (dayDiff < 30 && !checkSpecialRule(fromD, toD)) {
      setErrorIfDiff(
        useEnglish
          ? 'Minimum 30 days required, unless special conditions apply.'
          : '30日未満の期間は利用できません（特例条件除く）。'
      );
      return;
    }

    // (3) 在庫範囲内か
    if (!isDateRangeFullyAvailable(fromD, toD, availableRanges)) {
      setErrorIfDiff(
        useEnglish
          ? 'Some dates are unavailable within the selected period.'
          : '選択された期間の一部が利用不可です。'
      );
    } else {
      clearErrors('estimateData.period');
    }
  }, [
    fromDateValue,
    toDateValue,
    availableRanges,
    clearErrors,
    setError,
    useEnglish,
  ]);

  const setErrorIfDiff = (msg: string) => {
    const currentMsg = errors.estimateData?.period?.message as
      | string
      | undefined;
    if (currentMsg !== msg) {
      setError('estimateData.period', {
        type: 'manual',
        message: msg,
      });
    }
  };

  // From → To の最小値
  let toDateMin: string | undefined;
  if (fromDateValue) {
    const fromDateObj = parseLocalDate(fromDateValue);
    if (!isNaN(fromDateObj.getTime())) {
      const oneMonthLater = addMonths(fromDateObj, 1);
      oneMonthLater.setDate(oneMonthLater.getDate() - 1);
      toDateMin = format(oneMonthLater, 'yyyy-MM-dd');
    }
  }

  // カスタムバリデーション
  const validateFromDate = (value: string) => {
    if (!value) return true;
    const d = parseLocalDate(value);
    const inRange = availableRanges.some((r) => d >= r.start && d <= r.end);
    return (
      inRange ||
      (useEnglish
        ? 'From date is out of available range'
        : 'From 日付が利用可能期間外です')
    );
  };
  const validateToDate = (value: string) => {
    if (!value) return true;
    const d = parseLocalDate(value);
    const inRange = availableRanges.some((r) => d >= r.start && d <= r.end);
    return (
      inRange ||
      (useEnglish
        ? 'To date is out of available range'
        : 'To 日付が利用可能期間外です')
    );
  };

  const estimateErr = errors.estimateData || {};
  const periodError = estimateErr.period?.message;

  const initialFee = truncateToHundreds(price * 0.1);
  const cleaningFee = truncateToHundreds(price * 0.2);

  const inputStyles =
    'w-full border border-black mt-1 h-[27px] text-sm px-1 py-0';
  const selectStyles =
    'w-full border border-black mt-1 h-[27px] text-sm px-1 py-0';

  const maxGuests = capacity <= 0 ? 1 : capacity;
  const guestOptions = Array.from({ length: maxGuests }, (_, i) => i + 1);

  return (
    <div className="w-full max-md:max-w-md max-md:mx-auto mt-5 p-5 border border-black bg-[#F4F3E6]">
      {/* 利用可能期間の例 */}
      <div className="mb-4">
        {availableDatesPeriod.slice(0, 3).map((date, idx) => (
          <p key={idx} className="text-xs text-gray-800 mb-1">
            {date.period}
            {useEnglish ? ' is ' : '：'}
            <span className="underline">{date.status}</span>
          </p>
        ))}
      </div>

      {/* 期間フィールド */}
      <div className="mb-4">
        <label className="text-gray-800 flex items-center mb-1">
          <span className="text-base">{useEnglish ? 'Period' : '期間'}</span>
          <span className="text-base text-red-500 ml-1">*</span>
        </label>
        <div className="flex flex-col md:flex-row md:space-x-4">
          {/* fromDate */}
          <div className="w-full md:w-1/2 mb-2 md:mb-0">
            <label className="text-[10px] text-gray-600">
              {useEnglish ? 'From' : '入居日'}
            </label>
            <div className="relative">
              <input
                type="date"
                className={`${inputStyles} ${estimateErr.fromDate ? 'border-red-500' : ''}`}
                {...register('estimateData.fromDate', {
                  required: useEnglish
                    ? 'From date is required'
                    : '入居日が未入力です',
                  validate: { inRange: validateFromDate },
                })}
              />
              {estimateErr.fromDate && (
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-red-500 pr-5 pointer-events-none">
                  !
                </span>
              )}
            </div>
          </div>

          {/* toDate */}
          <div className="w-full md:w-1/2">
            <label className="text-[10px] text-gray-600">
              {useEnglish ? 'To' : '退去日'}
            </label>
            <div className="relative">
              <input
                type="date"
                className={`${inputStyles} ${estimateErr.toDate ? 'border-red-500' : ''}`}
                min={toDateMin}
                {...register('estimateData.toDate', {
                  required: useEnglish
                    ? 'To date is required'
                    : '退去日が未入力です',
                  validate: { inRange: validateToDate },
                })}
              />
              {estimateErr.toDate && (
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-red-500 pr-5 pointer-events-none">
                  !
                </span>
              )}
            </div>
          </div>
        </div>
        {periodError && (
          <p className="text-xs text-red-500 mt-2">* {periodError}</p>
        )}
      </div>

      {/* 人数 */}
      <div className="mb-7">
        <label className="text-gray-800 mb-1 flex items-center text-base">
          {useEnglish ? 'Guests' : '人数'}
          <span className="text-red-500 ml-1 flex items-center justify-center">
            *
          </span>
        </label>
        <div className="relative">
          <select
            className={`${selectStyles} ${estimateErr.guests ? 'border-red-500' : ''}`}
            {...register('estimateData.guests', {
              required: useEnglish ? 'Guests is required' : '人数が未入力です',
            })}
          >
            {guestOptions.map((num) => (
              <option key={num} value={num.toString()}>
                {num}
              </option>
            ))}
          </select>
          {estimateErr.guests && (
            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-red-500 pr-5">
              !
            </span>
          )}
        </div>
      </div>

      {/* コストサマリー */}
      <div className="mb-5 md:flex md:space-x-4">
        {/* 月額 */}
        <div className="mb-5 md:mb-0 md:flex-1">
          <CostSummary
            title={useEnglish ? 'Monthly Cost' : '一月の合計'}
            subtotal={price}
            insurances={[]}
          />
        </div>
        {/* 初期費用 */}
        <div className="md:flex-1">
          <CostSummary
            title={useEnglish ? 'Initial Cost' : '初期費用'}
            subtotal={0}
            insurances={[
              truncateToHundreds(price * 0.1),
              truncateToHundreds(price * 0.2),
            ]}
          />
        </div>
      </div>
      <p className="text-xs text-start">
        {useEnglish
          ? '*Includes utilities, Wi-Fi, etc.'
          : '※共益費、水光熱費、WIFI料金を含んでおります'}
      </p>
    </div>
  );
};
